// Feature flags for the web admin/client portal, backed by Firebase Remote
// Config. Mirrors the mobile app's `src/services/featureFlags.ts` surface
// (getBoolFlag/useBoolFlag etc.) so the two apps read flags the same way —
// only the SDK import and the override storage (localStorage vs MMKV) differ.
//
// What's different from mobile: this app *gates flags on the signed-in user*.
// `setFeatureFlagUserContext` pushes the user's role, agency and id to Remote
// Config as custom signals; targeting conditions on those signals are authored
// in the Firebase console. There is no separate owner signal — owner users are
// folded into the role as "Admin" (see AppLayout).

import { useSyncExternalStore } from "react";

import {
  fetchAndActivate,
  getValue,
  setCustomSignals,
} from "firebase/remote-config";

import { getRemoteConfigInstance } from "./firebase";
import { IsProduction } from "../helpers/config";
import { ALLOWED_VALUES, DEFAULTS, FLAGS } from "./featureFlags.defaults";

export { FLAGS } from "./featureFlags.defaults";

// Refresh at most every 5 minutes in dev (1 hour in prod) so flag changes
// propagate without spamming the backend. Firebase enforces a server-side
// minimum too.
const FETCH_INTERVAL_MS = IsProduction() ? 60 * 60 * 1000 : 5 * 60 * 1000;

// ----- Reactive subscription ----------------------------------------------

const listeners = new Set();

function emitFlagChange(updatedKeys) {
  for (const listener of listeners) listener(updatedKeys);
}

export function subscribeToFlagChanges(listener) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

// ----- Dev-only overrides --------------------------------------------------
// Lets a developer flip flags from the in-app panel without touching the
// Firebase console. Persisted to localStorage so overrides survive reloads.
// In production the setters are no-ops and getters fall through to Remote
// Config, so real users never see a persisted override.

const OVERRIDE_STORAGE_KEY = "moblyze:feature-flag-overrides/v1";
const overrides = new Map();

// Allowed outside production builds (local dev + Vercel preview), matching the
// mobile "dogfooding can override, prod users cannot" intent.
function overridesAllowed() {
  return !IsProduction();
}

function loadOverridesFromStorage() {
  if (!overridesAllowed()) return;
  try {
    const raw = window.localStorage.getItem(OVERRIDE_STORAGE_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === "object") {
      for (const [key, value] of Object.entries(parsed)) {
        if (
          typeof value === "boolean" ||
          typeof value === "string" ||
          typeof value === "number"
        ) {
          overrides.set(key, value);
        }
      }
    }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error("[featureFlags] Failed to load overrides", err);
  }
}

function persistOverrides() {
  try {
    const payload = Object.fromEntries(overrides);
    window.localStorage.setItem(OVERRIDE_STORAGE_KEY, JSON.stringify(payload));
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error("[featureFlags] Failed to persist overrides", err);
  }
}

export function setFlagOverride(key, value) {
  if (!overridesAllowed()) return;
  overrides.set(key, value);
  persistOverrides();
  emitFlagChange([key]);
}

export function clearFlagOverride(key) {
  if (!overridesAllowed()) return;
  if (!overrides.delete(key)) return;
  persistOverrides();
  emitFlagChange([key]);
}

export function clearAllFlagOverrides() {
  if (!overridesAllowed()) return;
  if (overrides.size === 0) return;
  const keys = [...overrides.keys()];
  overrides.clear();
  persistOverrides();
  emitFlagChange(keys);
}

export function getFlagOverride(key) {
  return overrides.get(key);
}

export function getAllFlagOverrides() {
  return Object.fromEntries(overrides);
}

export function getFlagType(key) {
  const def = DEFAULTS[key];
  if (typeof def === "string") return "string";
  if (typeof def === "number") return "number";
  return "boolean";
}

export function getRegisteredFlagKeys() {
  return Object.values(FLAGS);
}

// Enumerated allowed values for a flag, or null if open-ended. Used by the dev
// override panel to render a picker for typed string/number flags.
export function getAllowedValues(key) {
  return ALLOWED_VALUES[key] ?? null;
}

// ----- Initialisation ------------------------------------------------------

let initPromise = null;

export function initFeatureFlags() {
  if (initPromise) return initPromise;
  loadOverridesFromStorage();
  initPromise = (async () => {
    const rc = getRemoteConfigInstance();
    if (!rc) return; // No Firebase config — getters fall back to DEFAULTS.
    try {
      rc.settings.minimumFetchIntervalMillis = FETCH_INTERVAL_MS;
      // defaultConfig only accepts string/number/boolean values.
      rc.defaultConfig = { ...DEFAULTS };
      await fetchAndActivate(rc);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[featureFlags] init fetchAndActivate failed", err);
    }
    emitFlagChange([]);
  })();
  return initPromise;
}

/**
 * Push the signed-in user's properties to Remote Config as custom signals,
 * then re-resolve flag values against any console conditions that target them.
 * Call once the current user is known (see AppLayout).
 *
 * `role` is the effective role: AppLayout maps owner users to "Admin", so
 * there is no separate owner signal to gate on. `user_id` is there to turn a
 * flag on for one person (dogfooding in production) without widening it to
 * their whole role or agency.
 *
 * @param {{ role?: string, agencySlug?: string, userId?: string }} ctx
 */
export async function setFeatureFlagUserContext({ role, agencySlug, userId }) {
  const rc = getRemoteConfigInstance();
  if (!rc) return;
  // Custom signals are only evaluated server-side at fetch time, so the fetch
  // carrying the freshly-set signals MUST hit the server. The init fetch ran
  // before the user was known and primed the throttle (FETCH_INTERVAL_MS), so a
  // normal fetch here would be skipped and the console conditions on
  // role/agency_slug would never re-evaluate — leaving targeted flags at their
  // default. Bypass the throttle for this one fetch, then restore the interval.
  const previousInterval = rc.settings.minimumFetchIntervalMillis;
  try {
    await setCustomSignals(rc, {
      role: role ?? "",
      agency_slug: agencySlug ?? "",
      user_id: userId ?? "",
    });
    rc.settings.minimumFetchIntervalMillis = 0;
    await fetchAndActivate(rc);
    emitFlagChange([]);
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error("[featureFlags] setFeatureFlagUserContext failed", err);
  } finally {
    rc.settings.minimumFetchIntervalMillis = previousInterval;
  }
}

// ----- Reads ---------------------------------------------------------------

export function getBoolFlag(key) {
  const override = overrides.get(key);
  if (override !== undefined) return Boolean(override);
  const rc = getRemoteConfigInstance();
  if (!rc) return Boolean(DEFAULTS[key]);
  return getValue(rc, key).asBoolean();
}

export function getStringFlag(key) {
  const override = overrides.get(key);
  if (override !== undefined) return String(override);
  const rc = getRemoteConfigInstance();
  if (!rc) return String(DEFAULTS[key] ?? "");
  return getValue(rc, key).asString();
}

export function getNumberFlag(key) {
  const override = overrides.get(key);
  if (override !== undefined) return Number(override);
  const rc = getRemoteConfigInstance();
  if (!rc) return Number(DEFAULTS[key] ?? 0);
  return getValue(rc, key).asNumber();
}

export function useBoolFlag(key) {
  return useSyncExternalStore(
    subscribeToFlagChanges,
    () => getBoolFlag(key),
    () => getBoolFlag(key),
  );
}

export function useStringFlag(key) {
  return useSyncExternalStore(
    subscribeToFlagChanges,
    () => getStringFlag(key),
    () => getStringFlag(key),
  );
}

export function useNumberFlag(key) {
  return useSyncExternalStore(
    subscribeToFlagChanges,
    () => getNumberFlag(key),
    () => getNumberFlag(key),
  );
}
